import axios, { AxiosResponse } from 'axios';
import {
  SampleAssessmentSendBackResultResponse,
  SendBackAssessedExamTaskGroupSolutionsResponse,
  ExamTaskGroupSolutionScoreVersions,
  AssignToVerifierExamTaskGroupSolutionScoresResponseDto,
} from './scoring.types';
import EnvWindow from '../../utils/env';
import { AssessmentSessionProcessTypeId } from '../exams/assessmentSession/assessments.types';
import { SampleAssessmentExamTaskGroupSolutionIdsResponse } from '../scans/examTaskGroupSolution/examTaskGroupSolution.types';
import { addParam, addParamsList, transformResponsePreserveBigInts } from '../../utils/servicesHelper';
import { FlagTypes, FlagsDto } from '../../components/SystemSettings/SystemFlags/Subcomponents/Flag.types';
import { SessionDetailsResponse } from '../../components/ExamineeDetails/ExamineeDetails.types';

export async function updateScoreNoAnswer(
  solutionScoreId: string,
  noAnswer: boolean,
  examinerid: string,
): Promise<AxiosResponse<{ id: string }>> {
  return await axios.put(`${EnvWindow.REACT_APP_SCORING_API_URL}/PartialScore/NoAnswer`, {
    solutionScoreId,
    noAnswer,
    examinerid,
  });
}

export async function setPartialScore(
  partialScoreId: string,
  numberOfPoints: number | null,
  examinerid: string,
): Promise<AxiosResponse<{ id: string }>> {
  return await axios.put(`${EnvWindow.REACT_APP_SCORING_API_URL}/PartialScore`, {
    partialScoreId,
    numberOfPoints,
    examinerid,
  });
}

export async function cleanPartialScores(solutionScoreId: string): Promise<AxiosResponse> {
  return await axios.put(`${EnvWindow.REACT_APP_SCORING_API_URL}/SolutionScore/${solutionScoreId}/cleanPartialScores`);
}

export async function setAssessmentSessionEvent(assessmentSessionEventType: number): Promise<AxiosResponse> {
  return await axios.post(
    `${EnvWindow.REACT_APP_SCORING_API_URL}/AssessmentSessionEvent/${assessmentSessionEventType}`,
  );
}

export async function sendBackAssessedSolutions(
  examTaskGroupSolutionsScoreIds: string[],
): Promise<AxiosResponse<SendBackAssessedExamTaskGroupSolutionsResponse>> {
  return examTaskGroupSolutionsScoreIds.length === 1
    ? await axios.put(`${EnvWindow.REACT_APP_SCORING__EXAM_TASK_GROUP_SOLUTION_API_URL}/chosen`, {
        examTaskGroupSolutionsScoreIds,
      })
    : await axios.put(`${EnvWindow.REACT_APP_SCORING__EXAM_TASK_GROUP_SOLUTION_API_URL}/assessed`);
}

export async function sendBackVerifiedSolutions(
  examTaskGroupSolutionScoreId: string,
): Promise<AxiosResponse<SendBackAssessedExamTaskGroupSolutionsResponse>> {
  return await axios.patch(
    `${EnvWindow.REACT_APP_SCORING__EXAM_TASK_GROUP_SOLUTION_SCORE_API_URL}/${examTaskGroupSolutionScoreId}/verifier/sendBack`,
  );
}

export async function setFinalVerifierScore(
  examTaskGroupSolutionScoreId: string,
  examTaskGroupSolutionScoreVersionId: string,
): Promise<AxiosResponse> {
  return await axios.patch(
    `${EnvWindow.REACT_APP_SCORING__EXAM_TASK_GROUP_SOLUTION_SCORE_API_URL}/${examTaskGroupSolutionScoreId}/verified/${examTaskGroupSolutionScoreVersionId}`,
  );
}

export async function deleteExamTaskGroupSolutionAssignment(
  examTaskGroupSolutionScoreId: string[],
): Promise<AxiosResponse> {
  let params = '';
  params += addParamsList(
    'examTaskGroupSolutionScoreId',
    examTaskGroupSolutionScoreId.length > 0 ? examTaskGroupSolutionScoreId : [],
  );

  return await axios.delete(
    `${EnvWindow.REACT_APP_SCORING__EXAM_TASK_GROUP_SOLUTION_SCORE_API_URL}?examTaskGroupSolutionScore?examTaskGroupSolutionScoreId=${params}`,
  );
}

export async function deleteAllExamTaskGroupSolutionAssignment(
  deskId: string,
  functionEnum: string,
  assessmentSessionProcessTypeId: string,
  examTaskGroupSolutionScoreId?: string,
  status?: string,
  pointsFrom?: string,
  pointsTo?: string,
  progress?: string,
  downloadDateFrom?: string,
  downloadDateTo?: string,
  updateDateFrom?: string,
  updateDateTo?: string,
  isSpecialCase?: string,
  isControlProcessType?: string,
  isScoringAdjusted?: string,
  flag?: string,
  isCommentOnCriteria?: string,
  isCommentOnImage?: string,
  isCommentOffPage?: string,
  withMessage?: string,
): Promise<AxiosResponse> {
  const flagFilter =
    flag &&
    flag
      .split(',')
      .map((e) => FlagTypes.find((f) => f.name === e)!.id.toString())
      .join();

  let params = '';
  params += addParam('examTaskGroupSolutionScoreId', examTaskGroupSolutionScoreId ? examTaskGroupSolutionScoreId : '');
  params += addParam('status', status ? status : '');
  params += addParam('pointsFrom', pointsFrom);
  params += addParam('pointsTo', pointsTo);
  params += addParam('progress', progress ? progress : '');
  params += addParam('downloadDateFrom', downloadDateFrom ? downloadDateFrom : '');
  params += addParam('downloadDateTo', downloadDateTo ? downloadDateTo : '');
  params += addParam('updateDateFrom', updateDateFrom ? updateDateFrom : '');
  params += addParam('updateDateTo', updateDateTo ? updateDateTo : '');
  params += addParam('isSpecialCase', isSpecialCase ? isSpecialCase : '');
  params += addParam('isControlProcessType', isControlProcessType ? isControlProcessType : '');
  params += addParam('isScoringAdjusted', isScoringAdjusted ? isScoringAdjusted : '');
  params += addParam('flag', flag ? flagFilter : '');
  params += addParam('isCommentOnCriteria', isCommentOnCriteria ? isCommentOnCriteria : '');
  params += addParam('isCommentOnImage', isCommentOnImage ? isCommentOnImage : '');
  params += addParam('isCommentOffPage', isCommentOffPage ? isCommentOffPage : '');
  params += addParam('withMessage', withMessage ? withMessage : '');

  return await axios.delete(
    `${EnvWindow.REACT_APP_SCORING_API_URL}/AssessmentSession/Desk/${deskId}/Function/${functionEnum}/Process/${assessmentSessionProcessTypeId}?${params}`,
  );
}

export async function getAssignedAssessmentSessionProcess(
  examTaskGroupSolutionScoreId: string,
): Promise<AxiosResponse<AssessmentSessionProcessTypeId | null>> {
  return await axios.get(
    `${EnvWindow.REACT_APP_SCORING__EXAM_TASK_GROUP_SOLUTION_SCORE_API_URL}/${examTaskGroupSolutionScoreId}/AssessmentSessionProcessType`,
  );
}

export async function assignAssessmentSessionProcess(
  examTaskGroupSolutionScoreId: string,
  assessmentSessionProcessTypeId: AssessmentSessionProcessTypeId,
): Promise<boolean> {
  return await axios.patch(
    `${
      EnvWindow.REACT_APP_SCORING__EXAM_TASK_GROUP_SOLUTION_SCORE_API_URL
    }/${examTaskGroupSolutionScoreId}/AssessmentSessionProcessType/${assessmentSessionProcessTypeId.toString()}`,
  );
}

export async function deleteAssessmentSessionProcessAssignment(examTaskGroupSolutionScoreId: string): Promise<boolean> {
  return await axios.delete(
    `${EnvWindow.REACT_APP_SCORING__EXAM_TASK_GROUP_SOLUTION_SCORE_API_URL}/${examTaskGroupSolutionScoreId}/AssessmentSessionProcessType`,
  );
}

export async function getExamTaskGroupSolutionScoreIds(
  examinerId?: string,
  examinerZwoFunctionEnum?: string,
): Promise<SampleAssessmentExamTaskGroupSolutionIdsResponse> {
  const response = await axios.get(
    `${EnvWindow.REACT_APP_SCORING__EXAM_TASK_GROUP_SOLUTION_SCORE_API_URL}/idList${
      examinerId ? `?examinerId=${examinerId}` : ''
    }${examinerZwoFunctionEnum ? `&examinerZwoFunctionEnum=${examinerZwoFunctionEnum}` : ''}`,
  );

  return response.data;
}

export async function sendBackAssessedSampleScore(
  examTaskGroupSolutionsScoreIds: string[],
  examinerId?: string,
): Promise<AxiosResponse<SampleAssessmentSendBackResultResponse>> {
  return examTaskGroupSolutionsScoreIds.length === 1
    ? await axios.patch(
        `${EnvWindow.REACT_APP_SCORING__EXAM_TASK_GROUP_SOLUTION_SCORE_API_URL}/${examTaskGroupSolutionsScoreIds[0]}/SendBackSampleScore`,
      )
    : ///TODO => add endpoint for sendingback all RGZ
      await axios.patch(`${EnvWindow.REACT_APP_SCORING__EXAM_TASK_GROUP_SOLUTION_SCORE_API_URL}/SendBackSampleScore`, {
        examinerId,
      });
}

export async function assignFlagToExamTaskGroupSolutionScore(
  examTaskGroupSolutionScoreId: string,
  flags: FlagsDto[],
): Promise<AxiosResponse> {
  return await axios.patch(
    `${EnvWindow.REACT_APP_SCORING__EXAM_TASK_GROUP_SOLUTION_SCORE_API_URL}/${examTaskGroupSolutionScoreId}/Flags`,
    flags,
  );
}

export async function unassignFlagFromExamTaskGroupSolutionScore(
  examTaskGroupSolutionScoreId: string,
  flagDefinitionId?: number,
): Promise<AxiosResponse<boolean>> {
  return await axios.delete(
    `${EnvWindow.REACT_APP_SCORING__EXAM_TASK_GROUP_SOLUTION_SCORE_API_URL}/${examTaskGroupSolutionScoreId}${
      flagDefinitionId !== undefined ? `?flagDefinitionId=${flagDefinitionId}` : ''
    }`,
  );
}

export async function getExamineeSessionDetails(
  taskSolutionSheetId: string,
): Promise<AxiosResponse<SessionDetailsResponse>> {
  return await axios.get(
    `${EnvWindow.REACT_APP_SCORING_API_URL}/TaskSolutionSheet/${taskSolutionSheetId}/SessionDetails`,
  );
}

export async function getSesionStats(examSheetId: string): Promise<AxiosResponse> {
  return await axios.get(`${EnvWindow.REACT_APP_SCORING_API_URL}/Statistic/${examSheetId}/sesionStats`);
}

export async function assignToVerifier(
  examTaskGroupSolutionScoreId: string[],
): Promise<AxiosResponse<AssignToVerifierExamTaskGroupSolutionScoresResponseDto>> {
  return await axios.patch(
    `${EnvWindow.REACT_APP_SCORING__EXAM_TASK_GROUP_SOLUTION_SCORE_API_URL}/assignToVerifier`,
    examTaskGroupSolutionScoreId,
  );
}

export async function getExamTaskGroupSolutionScoreVersions(
  examTaskGroupSolutionScoreId: string,
): Promise<AxiosResponse<ExamTaskGroupSolutionScoreVersions>> {
  return await axios.get(
    `${EnvWindow.REACT_APP_SCORING__EXAM_TASK_GROUP_SOLUTION_SCORE_API_URL}/examTaskGroupSolutionScoreVersions/${examTaskGroupSolutionScoreId}`,
    { transformResponse: [transformResponsePreserveBigInts] },
  );
}
